import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { doc, getDoc, getDocFromServer, collection, getDocs, query, where, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import StudentOverview from "../components/StudentOverview";
import EHCPManager from "../components/EHCPManager";
// REMOVED: CurriculumList - deleted in teaching plan reset
import ProgressTracker from "../components/ProgressTracker";
import TermlyTargets from "../components/TermlyTargets";
import CurriculumAttainmentPanel from "../components/CurriculumAttainmentPanel";
import ProfessionalRecommendations from "../components/ProfessionalRecommendations";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from '../components/ui/card';
import ProvisionMap from "../components/ProvisionMap";
import StaffStrategyBriefs from "../components/StaffStrategyBriefs";
import SupportPlanApprovalPanel from "../components/SupportPlanApprovalPanel";
import ProfessionalInterventionAlerts from "../components/ProfessionalInterventionAlerts";
import TeachingPlanTabPanel from "../components/TeachingPlanTabPanel";
import PupilParentVoicePanel from "../components/PupilParentVoicePanel";
import CyclesOfSupportPanel from "../components/CyclesOfSupportPanel";
import StudentProvisionPanel from "../components/StudentProvisionPanel";
import StudentInterventionWaitlistPanel from "../components/StudentInterventionWaitlistPanel";
import SectionFProvisionPanel from "../components/SectionFProvisionPanel";
import TacMeetingsPanel from "../components/TacMeetingsPanel";
import { exportIspPdf } from "../lib/functions";
import toast from "react-hot-toast";
import { useSchoolId } from "../lib/auth/schoolContext";
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import SchoolPageHeader from "../components/school/SchoolPageHeader";
import CapabilityNotice from "../components/school/CapabilityNotice";
import { useSchoolCapabilities, SCHOOL_ACTIONS } from "../lib/auth/capabilities";
import { studentHasEhcp } from "../lib/students";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function StudentProfile() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interventionCount, setInterventionCount] = useState(0);
  const [studentInterventions, setStudentInterventions] = useState([]);
  const [interventionGroups, setInterventionGroups] = useState([]);
  const [showProvision, setShowProvision] = useState(false);
  const [provisionMapRefreshKey, setProvisionMapRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showOldInactive, setShowOldInactive] = useState(false);
  const { schoolId, loading: loadingSchoolId } = useSchoolId();
  const { can } = useSchoolCapabilities();
  const [currentTab, setCurrentTab] = useState('overview');
  const [downloadingIsp, setDownloadingIsp] = useState(false);
  const [removingEhcp, setRemovingEhcp] = useState(false);
  const tabListRef = useRef(null);
  const studentName = [student?.firstName, student?.lastName].filter(Boolean).join(' ').trim() || student?.name || 'this student';
  const hasEhcp = student ? studentHasEhcp(student) : false;
  const baseTabs = useMemo(() => [
    { id: 'overview', name: 'Overview' },
    { id: 'teaching-plan', name: 'Teaching Plan' },
    // REMOVED: attainment tab - CurriculumAttainment component deleted
    { id: 'ehcp', name: 'EHCP' },
    // REMOVED: curriculums tab - CurriculumList component deleted
    { id: 'progress', name: 'Progress & Targets' },
    { id: 'recommendations', name: 'Professional Reports' },
    { id: 'interventions', name: 'Interventions' },
    { id: 'provision', name: 'Provision' },
  ], []);
  // Count of legacy intervention groups on the student document (from test/older data model)
  const legacyGroupCount = Array.isArray(student?.interventionGroups) ? student.interventionGroups.length : 0;

  const refreshStudent = React.useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!id || loadingSchoolId) return;
    try {
      if (!silent) setLoading(true);
      const docRef = doc(db, "students", id);
      let docSnap;
      try {
        docSnap = await getDocFromServer(docRef);
      } catch (serverErr) {
        console.warn('Server fetch failed, falling back to cache:', serverErr?.message);
        docSnap = await getDoc(docRef);
      }

      if (docSnap.exists()) {
        const studentData = { id: docSnap.id, ...docSnap.data() };
        // Enforce tenant isolation at UI layer too
        if (studentData.schoolId && schoolId && studentData.schoolId !== schoolId) {
          console.warn('Access blocked: student belongs to different school', { requested: id, studentSchool: studentData.schoolId, callerSchool: schoolId });
          setStudent(null);
        } else {
          setStudent(studentData);

          // Fetch intervention groups this student belongs to
          if (studentData.interventionGroups && Array.isArray(studentData.interventionGroups)) {
            const groupPromises = studentData.interventionGroups.map(async (groupId) => {
              try {
                const groupRef = doc(db, 'interventionGroups', groupId);
                const groupSnap = await getDoc(groupRef);
                if (groupSnap.exists()) {
                  return { id: groupSnap.id, ...groupSnap.data() };
                }
                return null;
              } catch (error) {
                console.error('Error fetching intervention group:', groupId, error);
                return null;
              }
            });
            const groups = await Promise.all(groupPromises);
            setInterventionGroups(groups.filter(g => g !== null));
          }
        }
      } else {
        setStudent(null);
      }
    } catch (error) {
      console.error("Error fetching student:", error);
      setStudent(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, schoolId, loadingSchoolId]);

  useEffect(() => {
    refreshStudent();
  }, [refreshStudent]);

  const openEhcpTab = () => {
    setCurrentTab('ehcp');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'ehcp');
      return next;
    });
  };

  const handleAddEhcp = () => {
    openEhcpTab();
    toast('Upload the EHCP PDF on the EHCP tab to register this pupil.', { icon: '📄' });
  };

  const handleRemoveEhcp = async () => {
    const confirmed = window.confirm(
      'Are you sure this student no longer has an EHCP?\n\nThis will remove their EHCP status but keep any uploaded documents and extracted data for your records.'
    );
    if (!confirmed) return;

    setRemovingEhcp(true);
    const toastId = toast.loading('Updating EHCP status...');
    try {
      await updateDoc(doc(db, 'students', id), {
        hasEHCP: false,
        'ehcp.hasEHCP': false,
        'ehcp.removedAt': serverTimestamp(),
      });
      await refreshStudent();
      toast.success('EHCP status removed', { id: toastId });
    } catch (err) {
      console.error('Error removing EHCP status:', err);
      toast.error(err?.message || 'Could not update EHCP status', { id: toastId });
    } finally {
      setRemovingEhcp(false);
    }
  };

  const parseDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const handleDownloadIsp = async () => {
    if (!id) return;
    setDownloadingIsp(true);
    const toastId = toast.loading('Generating Individual Support Plan...');
    try {
      const { data } = await exportIspPdf({ studentId: id });
      if (!data?.downloadUrl) {
        throw new Error('No download URL returned');
      }
      const filename = data.filename || `ISP-${studentName || id}.pdf`;
      // The function returns a base64 data URL; trigger a true download
      // by anchoring it rather than opening in a new tab.
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('ISP downloaded', { id: toastId });
    } catch (err) {
      console.error('Error generating ISP PDF:', err);
      toast.error(err?.message || 'Could not generate ISP', { id: toastId });
    } finally {
      setDownloadingIsp(false);
    }
  };

  const isWithinLastTwoTerms = (startDate, endDate, termWeeks = 12) => {
    const now = new Date();
    const refDate = parseDate(endDate) || parseDate(startDate);
    if (!refDate) return false;
    const msInWeek = 7 * 24 * 60 * 60 * 1000;
    const diffWeeks = (now.getTime() - refDate.getTime()) / msInWeek;
    return diffWeeks <= termWeeks * 2;
  };

  useEffect(() => {
    // Auto-open provision map if requested and student has interventions
    if (searchParams.get('openProvision') === '1') {
      setShowProvision(true);
      setCurrentTab('provision');
    }
  }, [searchParams]);

  useEffect(() => {
    const qpTab = searchParams.get('tab');
    if (qpTab === 'graduated') {
      setCurrentTab('overview');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'overview');
        return next;
      }, { replace: true });
      return;
    }
    if (qpTab && baseTabs.some(t => t.id === qpTab)) {
      setCurrentTab(qpTab);
    }
  }, [searchParams, baseTabs, setSearchParams]);


  useEffect(() => {
    const fetchInterventions = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'interventions'),
          where('schoolId', '==', schoolId),
          where('students', 'array-contains', id)
        ));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setStudentInterventions(list);
        setInterventionCount(list.length);
      } catch (e) {
        console.warn('Could not fetch interventions for student', id, e);
        setStudentInterventions([]);
        setInterventionCount(0);
      }
    };
    if (id && schoolId) fetchInterventions();
  }, [id, schoolId]);





  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600 dark:text-gray-400">Loading Individual Support Plan...</p>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Student Not Found</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>The student with ID "{id}" could not be found in the database.</p>
                  <p className="mt-1">Please check the URL or return to the dashboard to view existing students.</p>
                </div>
                <div className="mt-4">
                  <Link 
                    to="/dashboard" 
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Return to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <SchoolPageHeader
          title={`${studentName}, Individual Support Plan`}
          description="Comprehensive SEN record: pupil and parent voice, needs, interventions, EHCP evidence, and APDR cycles. Downloadable for EHCNA requests and parent meetings."
          icon={ChevronDownIcon}
          breadcrumbs={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Individual Support Plans", href: "/isps" },
            { label: studentName },
          ]}
          actions={[
            { label: downloadingIsp ? 'Generating...' : 'Download ISP', onClick: handleDownloadIsp, disabled: downloadingIsp },
            { label: 'One-Page Profile', href: `/students/${id}/one-page-profile`, variant: 'secondary' },
            can(SCHOOL_ACTIONS.MANAGE_EHCP_UPLOAD) && !hasEhcp
              ? { label: 'Add EHCP', onClick: handleAddEhcp, variant: 'secondary' }
              : null,
            can(SCHOOL_ACTIONS.MANAGE_EHCP_UPLOAD) && hasEhcp
              ? { label: removingEhcp ? 'Removing...' : 'Remove EHCP', onClick: handleRemoveEhcp, disabled: removingEhcp, variant: 'danger' }
              : null,
            can(SCHOOL_ACTIONS.GENERATE_EHCNA) && !hasEhcp
              ? { label: 'Start EHCNA Request', href: `/ehcna/new?studentId=${id}`, variant: 'secondary' }
              : null,
          ].filter(Boolean)}
        />

        {!can(SCHOOL_ACTIONS.GENERATE_EHCNA) && (
          <CapabilityNotice
            title="EHCNA generation is SENCO-only"
            body="You can still review EHCP data and progress evidence here. Ask your SENCO to generate or submit EHCNA content."
          />
        )}

        {/* Identity Header */}
        {student && (
          <Card className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h1>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{student.yearGroup} • {student.phase}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={classNames('inline-flex items-center px-2 py-1 text-xs rounded-full border', hasEhcp ? 'bg-green-50 text-green-800 border-green-200' : 'bg-blue-50 text-blue-800 border-blue-200')}>
                    {hasEhcp ? 'EHCP' : 'SEN Support (ISP)'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Navigation + Panel (connected frame) */}
        <div className="hidden sm:block">
          <div className="relative">
            <button
              type="button"
              aria-label="Scroll tabs left"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 border rounded-full w-8 h-8 flex items-center justify-center shadow"
              onClick={() => tabListRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Scroll tabs right"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 border rounded-full w-8 h-8 flex items-center justify-center shadow"
              onClick={() => tabListRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
            >
              ›
            </button>
            <nav
              aria-label="Tabs"
              role="tablist"
              aria-orientation="horizontal"
              ref={tabListRef}
              className="flex space-x-2 bg-white dark:bg-gray-800 border rounded-t-lg px-2 pt-2 overflow-x-auto whitespace-nowrap scrollbar-thin"
            >
            {baseTabs.map((tab) => {
              const isCurrent = tab.id === currentTab;
              const tabId = `tab-${tab.id}`;
              const panelId = `panel-${tab.id}`;
              return (
                <button
                  key={tab.id}
                  id={tabId}
                  role="tab"
                  aria-controls={panelId}
                  aria-selected={isCurrent}
                  tabIndex={isCurrent ? 0 : -1}
                  onClick={() => {
                    setCurrentTab(tab.id);
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set('tab', tab.id);
                      return next;
                    });
                  }}
                  className={classNames(
                    isCurrent
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 border-indigo-200'
                      : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 border-transparent',
                    'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium border'
                  )}
                >
                  {tab.name}
                </button>
              );
            })}
            </nav>
          </div>
          <section role="tabpanel" aria-labelledby={`tab-${currentTab}`} id={`panel-${currentTab}`} className="bg-white dark:bg-gray-800 border-x border-b rounded-b-lg p-4">
            {/* Tab Content */}
            {student && currentTab === 'overview' && (
              <div className="space-y-4">
                <StudentOverview 
                  student={student}
                  hideIdentityHeader={true}
                  activeInterventions={studentInterventions.filter(i => (i.status || 'active') === 'active')}
                />
                <PupilParentVoicePanel student={student} onSaved={refreshStudent} />
                <TacMeetingsPanel studentId={id} />
                <CyclesOfSupportPanel student={student} onSaved={refreshStudent} />
                <SupportPlanApprovalPanel studentId={id} />
                <ProfessionalInterventionAlerts studentId={id} />
                <StaffStrategyBriefs studentId={id} interventions={studentInterventions} />
              </div>
            )}

            {student && currentTab === 'teaching-plan' && (
              <TeachingPlanTabPanel studentId={id} />
            )}

            {/* REMOVED: attainment tab - CurriculumAttainment component deleted */}

            {student && currentTab === 'ehcp' && (
              <div>
                {!hasEhcp && can(SCHOOL_ACTIONS.MANAGE_EHCP_UPLOAD) && (
                  <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
                    <p className="font-medium">Register this pupil as EHCP</p>
                    <p className="mt-1 text-indigo-800">
                      Upload their EHCP PDF below. Once uploaded, they will appear as an EHCP pupil across the system.
                    </p>
                  </div>
                )}
                <EHCPManager
                  student={student}
                  studentId={id}
                  onEhcpStatusChange={() => refreshStudent({ silent: true })}
                />
              </div>
            )}

            {/* REMOVED: curriculums tab - CurriculumList component deleted */}

            {student && currentTab === 'progress' && (
              <div className="space-y-6">
                <CurriculumAttainmentPanel studentId={id} yearGroup={student.yearGroup} phase={student.phase} />
                <ProgressTracker studentId={id} studentName={studentName} />
                <TermlyTargets studentId={id} />
              </div>
            )}

            {student && currentTab === 'recommendations' && (
              <div>
                <ProfessionalRecommendations studentId={id} />
              </div>
            )}

            {student && currentTab === 'interventions' && (
              <div>
                <div className="mb-4 space-y-4">
                  <StudentInterventionWaitlistPanel studentId={id} />
                  {hasEhcp && (
                    <SectionFProvisionPanel
                      studentId={id}
                      student={student}
                      filterDestination="intervention"
                      showUnmet={false}
                    />
                  )}
                </div>
                <div className="mb-4">
                  <StaffStrategyBriefs studentId={id} interventions={studentInterventions} />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Interventions</h3>
                  <Link to={`/student/${id}/interventions/history`} className="text-sm text-blue-600 hover:underline">View full history</Link>
                </div>
                {studentInterventions.length === 0 ? (
                  interventionGroups.length > 0 ? (
                    <div className="space-y-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        This student is assigned to {interventionGroups.length} intervention group{interventionGroups.length > 1 ? 's' : ''}.
                      </div>
                      <div className="space-y-3">
                        {interventionGroups.map((group) => (
                          <div key={group.id} className="p-4 border border-purple-200 dark:border-purple-700 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white">{group.name}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{group.interventionType}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                group.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                group.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              }`}>
                                {group.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{group.description}</p>
                            {group.schedule && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {group.schedule.frequency} • {group.schedule.duration}
                              </p>
                            )}
                            {group.targetedNeeds && group.targetedNeeds.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {group.targetedNeeds.map((need, idx) => (
                                  <span key={idx} className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-200 rounded">
                                    {need}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <Link to="/intervention-groups" className="text-sm text-blue-600 hover:underline">
                        View all intervention groups →
                      </Link>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600 dark:text-gray-300">No interventions recorded for this student.</div>
                  )
                ) : (
                  <div className="space-y-4">
                    {studentInterventions.filter(i => (i.status || 'active') === 'active').length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Active</div>
                        <div className="divide-y rounded border border-gray-200 dark:border-gray-700">
                          {studentInterventions
                            .filter(i => (i.status || 'active') === 'active')
                            .map(i => (
                              <div key={i.id} className="p-3 flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">{i.name || 'Intervention'}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300">{i.type || ''}</div>
                                  <div className="text-xs text-gray-500">{i.startDate || '-'} → {i.endDate || 'ongoing'}</div>
                                </div>
                                <div className="text-xs text-gray-700 dark:text-gray-300">
                                  {i.leadStaffName ? `Lead: ${i.leadStaffName}${i.leadStaffEmail ? ' ('+i.leadStaffEmail+')' : ''}` : 'Lead: Unassigned'}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {studentInterventions.filter(i => (i.status || '') !== 'active' && isWithinLastTwoTerms(i.startDate, i.endDate)).length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Recently Completed (last two terms)</div>
                        <div className="divide-y rounded border border-gray-200 dark:border-gray-700">
                          {studentInterventions
                            .filter(i => (i.status || '') !== 'active' && isWithinLastTwoTerms(i.startDate, i.endDate))
                            .map(i => (
                              <div key={i.id} className="p-3 flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">{i.name || 'Intervention'}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300">{i.type || ''}</div>
                                  <div className="text-xs text-gray-500">{i.startDate || '-'} → {i.endDate || '-'}</div>
                                </div>
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">inactive</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {studentInterventions.filter(i => (i.status || '') !== 'active' && !isWithinLastTwoTerms(i.startDate, i.endDate)).length > 0 && (
                      <div>
                        <button
                          type="button"
                          onClick={() => setShowOldInactive(v => !v)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {showOldInactive ? 'Hide inactive interventions' : `Inactive interventions (${studentInterventions.filter(i => (i.status || '') !== 'active' && !isWithinLastTwoTerms(i.startDate, i.endDate)).length})`}
                        </button>
                        {showOldInactive && (
                          <div className="mt-2 divide-y rounded border border-gray-200 dark:border-gray-700">
                            {studentInterventions
                              .filter(i => (i.status || '') !== 'active' && !isWithinLastTwoTerms(i.startDate, i.endDate))
                              .map(i => (
                                <div key={i.id} className="p-3 flex items-center justify-between">
                                  <div>
                                    <div className="font-medium text-gray-900 dark:text-white">{i.name || 'Intervention'}</div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300">{i.type || ''}</div>
                                    <div className="text-xs text-gray-500">{i.startDate || '-'} → {i.endDate || '-'}</div>
                                  </div>
                                  <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">inactive</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {student && currentTab === 'provision' && (
              <div className="space-y-6">
                <StudentProvisionPanel
                  studentId={id}
                  student={student}
                  onProvisionChange={() => setProvisionMapRefreshKey((k) => k + 1)}
                />
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Costed provision map</h3>
                    <button
                      onClick={() => setShowProvision((v) => !v)}
                      className="inline-flex items-center rounded-xl bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                    >
                      {showProvision ? 'Hide provision map' : 'Show provision map'}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    SENCO-only costing for student provision (pastoral, TA, equipment) and pull-out interventions.
                  </p>
                  {showProvision && (
                    <ProvisionMap studentId={id} refreshKey={provisionMapRefreshKey} />
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Mobile tabs (select) */}
        <div className="grid grid-cols-1 sm:hidden">
          <select
            value={baseTabs.find((t) => t.id === currentTab)?.name}
            aria-label="Select a tab"
            onChange={(e) => {
              const next = baseTabs.find(t => t.name === e.target.value)?.id || 'overview';
              setCurrentTab(next);
              setSearchParams(prev => {
                const qp = new URLSearchParams(prev);
                qp.set('tab', next);
                return qp;
              });
            }}
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-gray-800/50 dark:text-gray-100 dark:outline-white/10 dark:*:bg-gray-800 dark:focus:outline-indigo-500"
          >
            {baseTabs.map((tab) => (
              <option key={tab.id}>{tab.name}</option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500 dark:fill-gray-400"
          />
        </div>
      </div>
    </AppLayout>
  );
}
